import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { ArrowDown, Github, Linkedin, CheckCircle, Zap } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { scrollToSection } from '@/lib/utils'
import { useParallax } from '@/hooks/useScrollAnimation'

// Tech logos as simple badges
const techStack = ['Java', 'Spring Boot', 'React', 'TypeScript', 'PostgreSQL']

export function Hero() {
  const { t } = useTranslation()
  const [currentRoleIndex, setCurrentRoleIndex] = useState(0)
  const [displayText, setDisplayText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const parallaxOffset = useParallax(0.3)

  // Get localized roles
  const roles = t('hero.roles', { returnObjects: true }) as string[]

  // Typewriter effect
  useEffect(() => {
    const currentRole = roles[currentRoleIndex]
    const typeSpeed = isDeleting ? 50 : 100
    const pauseTime = 2000

    if (!isDeleting && displayText === currentRole) {
      const timeout = setTimeout(() => setIsDeleting(true), pauseTime)
      return () => clearTimeout(timeout)
    }

    if (isDeleting && displayText === '') {
      setIsDeleting(false)
      setCurrentRoleIndex((prev) => (prev + 1) % roles.length)
      return
    }

    const timeout = setTimeout(() => {
      setDisplayText((prev) =>
        isDeleting ? prev.slice(0, -1) : currentRole.slice(0, prev.length + 1)
      )
    }, typeSpeed)

    return () => clearTimeout(timeout)
  }, [displayText, isDeleting, currentRoleIndex, roles])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  }

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16 lg:pt-20"
      aria-labelledby="hero-heading"
    >
      {/* Animated Background */}
      <div className="absolute inset-0 -z-10">
        {/* Gradient base */}
        <div
          className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5"
          style={{ transform: `translateY(${parallaxOffset}px)` }}
        />

        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(var(--color-foreground) 1px, transparent 1px), linear-gradient(90deg, var(--color-foreground) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />

        {/* Floating shapes - CSS animations for GPU acceleration */}
        <div
          className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-full blur-3xl animate-float-slow will-change-transform"
        />
        <div
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-secondary/20 to-accent/20 rounded-full blur-3xl animate-float-medium will-change-transform"
        />
        <div
          className="absolute top-1/2 right-1/3 w-48 h-48 bg-gradient-to-r from-accent/20 to-primary/20 rounded-full blur-3xl animate-float-fast will-change-transform"
        />

        {/* Decorative circles */}
        <div className="absolute top-20 right-20 w-4 h-4 bg-primary/30 rounded-full" />
        <div className="absolute bottom-32 left-32 w-6 h-6 bg-secondary/30 rounded-full" />
        <div className="absolute top-1/3 right-1/4 w-3 h-3 bg-accent/30 rounded-full" />
      </div>

      {/* Content */}
      <motion.div
        className="container mx-auto px-5 sm:px-6 lg:px-8 text-center py-8 sm:py-0"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Badge */}
        <motion.div
          variants={itemVariants}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm sm:text-base font-medium mb-6 sm:mb-8"
        >
          <Zap className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
          <span>{t('hero.greeting')}</span>
        </motion.div>

        <motion.h1
          id="hero-heading"
          variants={itemVariants}
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4 sm:mb-6 leading-tight"
        >
          <span className="bg-gradient-to-r from-foreground via-primary to-secondary bg-clip-text text-transparent">
            {t('hero.name')}
          </span>
        </motion.h1>

        <motion.div variants={itemVariants} className="h-10 sm:h-12 md:h-14 mb-4 sm:mb-6">
          <span className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            {displayText}
            <motion.span
              animate={{ opacity: [1, 0] }}
              transition={{ duration: 0.5, repeat: Infinity, repeatType: 'reverse' }}
              className="inline-block w-0.5 sm:w-1 h-6 sm:h-8 md:h-10 bg-primary ml-1 align-middle"
              aria-hidden="true"
            />
          </span>
        </motion.div>

        <motion.p
          variants={itemVariants}
          className="text-base sm:text-lg md:text-xl text-foreground-secondary max-w-2xl mx-auto mb-4 px-2 leading-relaxed"
        >
          {t('hero.subtitle')}
        </motion.p>

        {/* Target Audience */}
        <motion.p
          variants={itemVariants}
          className="text-sm sm:text-base text-foreground-muted max-w-xl mx-auto mb-6 sm:mb-8 px-2"
        >
          {t('hero.targetAudience')}
        </motion.p>

        {/* Availability Indicator */}
        <motion.div
          variants={itemVariants}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-success/10 border border-success/30 rounded-full text-success text-sm sm:text-base font-medium mb-4 sm:mb-6"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-success"></span>
          </span>
          <span>{t('hero.availability')}</span>
        </motion.div>

        {/* USP Pills */}
        <motion.div
          variants={itemVariants}
          className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-6 sm:mb-8 px-2"
        >
          {[
            { icon: CheckCircle, text: t('hero.usps.consultation') },
            { icon: CheckCircle, text: t('hero.usps.response') },
            { icon: CheckCircle, text: t('hero.usps.madeIn') },
          ].map((item, index) => (
            <motion.div
              key={index}
              className="flex items-center gap-2 px-3 sm:px-4 py-2.5 bg-background-secondary rounded-full text-xs sm:text-sm text-foreground-secondary"
              whileHover={{ scale: 1.05, backgroundColor: 'var(--color-primary)', color: 'white' }}
              transition={{ duration: 0.2 }}
            >
              <item.icon className="w-4 h-4 text-success flex-shrink-0" aria-hidden="true" />
              <span>{item.text}</span>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8 sm:mb-10 px-4 sm:px-0"
        >
          <Button
            size="lg"
            onClick={() => scrollToSection('contact')}
            className="w-full sm:w-auto group shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 text-base sm:text-lg"
          >
            {t('hero.cta.contact')}
            <span className="ml-2 inline-block group-hover:translate-x-1 transition-transform">
              →
            </span>
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={() => scrollToSection('projects')}
            className="w-full sm:w-auto text-base sm:text-lg"
          >
            {t('hero.cta.projects')}
          </Button>
        </motion.div>

        {/* Tech Stack Badges */}
        <motion.div variants={itemVariants} className="mb-8 sm:mb-10">
          <p className="text-foreground-muted text-xs sm:text-sm mb-3 sm:mb-4">{t('hero.techStack')}</p>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 px-2">
            {techStack.map((tech) => (
              <div
                key={tech}
                className="px-3 sm:px-4 py-2 sm:py-2.5 bg-background-secondary border border-border rounded-lg text-xs sm:text-sm font-medium text-foreground-secondary hover:scale-105 hover:border-primary hover:text-primary transition-all duration-200"
              >
                {tech}
              </div>
            ))}
          </div>
        </motion.div>

        {/* Social Links */}
        <motion.div variants={itemVariants} className="flex items-center justify-center gap-4 sm:gap-5 pb-20 sm:pb-24">
          <a
            href="https://github.com/alugor"
            target="_blank"
            rel="noopener noreferrer"
            className="p-3.5 sm:p-4 rounded-full bg-background-secondary border border-border hover:bg-primary hover:text-white hover:border-primary hover:scale-110 active:scale-95 transition-all duration-200 min-w-[48px] min-h-[48px] flex items-center justify-center"
            aria-label="GitHub"
          >
            <Github className="w-5 h-5 sm:w-6 sm:h-6" />
          </a>
          <a
            href="https://linkedin.com/in/jonas-fackelmeyer"
            target="_blank"
            rel="noopener noreferrer"
            className="p-3.5 sm:p-4 rounded-full bg-background-secondary border border-border hover:bg-primary hover:text-white hover:border-primary hover:scale-110 active:scale-95 transition-all duration-200 min-w-[48px] min-h-[48px] flex items-center justify-center"
            aria-label="LinkedIn"
          >
            <Linkedin className="w-5 h-5 sm:w-6 sm:h-6" />
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2">
        <button
          onClick={() => scrollToSection('about')}
          className="flex flex-col items-center gap-2 text-foreground-muted hover:text-primary transition-colors p-2 min-h-[48px] animate-bounce-slow"
          aria-label={t('hero.scroll')}
        >
          <span className="text-xs sm:text-sm font-medium">{t('hero.scroll')}</span>
          <ArrowDown className="w-5 h-5" />
        </button>
      </div>
    </section>
  )
}
